import { useEffect } from "react";
import { FloatingPosition, Indicator, Popover } from "@mantine/core";
import { IndicatorPosition } from "@mantine/core/lib/components/Indicator/Indicator.types";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";

interface Props {
  featureId: string;
  children: React.ReactNode;
  indicatorPosition?: IndicatorPosition;
  popoverContent?: React.ReactNode;
  popoverContentPosition?: FloatingPosition;
  hasSeen?: boolean; // Manage the state externally instead of with a popover internally
  disabled?: boolean;
}

const FEATURES_SEEN_KEY = "NewFeatureIndicator::seenFeatures";

export const NewFeatureIndicator = ({
  featureId,
  children,
  popoverContent,
  hasSeen = false,
  disabled = false,
  indicatorPosition = "top-start",
  popoverContentPosition = "top-start",
}: Props) => {
  const [featuresSeen, setFeaturesSeen] = useLocalStorage({
    key: FEATURES_SEEN_KEY,
    defaultValue: new Set<string>(),
    serialize: (value) => JSON.stringify([...value]),
    deserialize: (localStorageValue) =>
      new Set([...JSON.parse(localStorageValue ?? "[]")]),
  });

  useEffect(() => {
    if (hasSeen) {
      setFeaturesSeen((value) => new Set([...value, featureId]));
    }
  }, [featureId, hasSeen, setFeaturesSeen]);

  const [showPopover, popoverControls] = useDisclosure(false);

  if (disabled) return children;
  if (!showPopover && featuresSeen.has(featureId)) return children;

  return (
    <Popover
      width={360}
      position={popoverContentPosition}
      withArrow
      shadow="sm"
      opened={showPopover}
      onChange={popoverControls.toggle}
      onClose={() => {
        setFeaturesSeen((value) => new Set([...value, featureId]));
      }}
    >
      <Popover.Target>
        <Indicator
          withBorder
          processing
          color="lime"
          size={12}
          position={indicatorPosition}
          style={{ cursor: "pointer" }}
          onClick={popoverControls.open}
          data-fs-element="New feature indicator"
          data-fs-properties-schema={JSON.stringify({
            "data-feature-id": { type: "str", name: "featureId" },
          })}
          data-feature-id={featureId}
        >
          {children}
        </Indicator>
      </Popover.Target>
      {popoverContent && <Popover.Dropdown>{popoverContent}</Popover.Dropdown>}
    </Popover>
  );
};
